"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import ProductForm from "./ProductForm";
import ProductImage from "./ProductImage";
import JotformReviewEmbed from "@/components/JotformReviewEmbed";
import EmployeeAvatar from "@/components/EmployeeAvatar";
import PhoneLink from "@/components/PhoneLink";
import {
  FaHospital,
  FaPills,
  FaInfoCircle,
  FaFlask,
  FaClipboardList,
  FaFileAlt,
  FaShippingFast,
  FaStar,
} from "react-icons/fa";

const TAB_SECTIONS = [
  "pharmacy-info",
  "drug-description",
  "product-info",
  "active-ingredients",
  "directions",
  "instructions",
  "shipping",
  "reviews",
];

const SECTION_META = {
  "pharmacy-info":      { label: "Pharmacy Info",      Icon: FaHospital },
  "drug-description":   { label: "Drug Description",   Icon: FaPills },
  "product-info":       { label: "Product Info",       Icon: FaInfoCircle },
  "active-ingredients": { label: "Active Ingredients", Icon: FaFlask },
  "directions":         { label: "Directions",         Icon: FaClipboardList },
  "instructions":       { label: "Instructions",       Icon: FaFileAlt },
  "shipping":           { label: "Shipping",           Icon: FaShippingFast },
  "reviews":            { label: "Reviews",            Icon: FaStar },
};

const getReviewRating = (review) => {
  const ratings = review?.ratings || {};
  const values = ["quality", "value", "price"]
    .map((key) => Number(ratings[key] ?? 0))
    .filter((n) => n > 0);
  if (!values.length) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const StarRow = ({ value = 0, label = "Review rating" }) => {
  const roundedValue = Math.round(Number(value) || 0);
  return (
    <div className="star-row" aria-label={`${roundedValue} out of 5 stars`}>
      <div className="stars" aria-hidden="true">
        {[1, 2, 3, 4, 5].map((idx) => (
          <svg
            key={idx}
            viewBox="0 0 20 20"
            className={idx <= roundedValue ? "star filled" : "star"}
          >
            <path d="M10 1.5l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.8-5.2 2.8 1-5.8-4.2-4.1 5.8-.8L10 1.5z" />
          </svg>
        ))}
      </div>
      <span className="star-rating-value">{roundedValue}/5</span>
      <span className="star-rating-label">{label}</span>
    </div>
  );
};

const ReviewsBlock = ({ reviews = [] }) => {
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;

    const maxScrollLeft = viewport.scrollWidth - viewport.clientWidth;
    setCanScrollLeft(viewport.scrollLeft > 2);
    setCanScrollRight(viewport.scrollLeft < maxScrollLeft - 2);
  }, []);

  useEffect(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport) return;

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(updateScrollState)
        : null;

    updateScrollState();
    const rafId = window.requestAnimationFrame(updateScrollState);
    const timerId = window.setTimeout(updateScrollState, 250);

    viewport.addEventListener("scroll", updateScrollState, { passive: true });
    window.addEventListener("resize", updateScrollState);
    resizeObserver?.observe(viewport);
    if (track) {
      resizeObserver?.observe(track);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timerId);
      resizeObserver?.disconnect();
      viewport.removeEventListener("scroll", updateScrollState);
      window.removeEventListener("resize", updateScrollState);
    };
  }, [reviews.length, updateScrollState]);

  useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollLeft = 0;
    }
    updateScrollState();
  }, [reviews.length, updateScrollState]);

  const scrollByCard = (direction) => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    const firstCard = track.querySelector(".review-carousel-card");
    if (!firstCard) return;

    const cardWidth = firstCard.getBoundingClientRect().width;
    const gap = Number.parseFloat(window.getComputedStyle(track).columnGap || "0") || 0;
    const amount = (cardWidth + gap) * direction;
    viewport.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <div className="reviews-carousel" aria-label="Customer reviews carousel">
      <div className="reviews-carousel-controls">
        <button
          type="button"
          className="reviews-carousel-arrow"
          onClick={() => scrollByCard(-1)}
          aria-label="Scroll reviews left"
          disabled={!canScrollLeft}
        >
          ‹
        </button>
        <button
          type="button"
          className="reviews-carousel-arrow"
          onClick={() => scrollByCard(1)}
          aria-label="Scroll reviews right"
          disabled={!canScrollRight}
        >
          ›
        </button>
      </div>

      <div className="reviews-carousel-viewport" ref={viewportRef} tabIndex={0}>
        <div className="reviews-list" ref={trackRef}>
          {reviews.length ? (
            reviews.map((review, idx) => (
              <div key={idx} className="review-carousel-card">
                <article className="review-card">
                  <header className="review-header">
                    <h3>{review.name || "Anonymous"}</h3>
                    {review.timestamp ? (
                      <p className="review-meta">
                        <time>{review.timestamp}</time>
                      </p>
                    ) : null}
                  </header>
                  <div className="review-stars">
                    <StarRow value={getReviewRating(review)} />
                  </div>
                  <p className="review-body">{review.description}</p>
                </article>
              </div>
            ))
          ) : (
            <div className="review-carousel-card">
              <article className="review-card">
                <p className="reviews-empty">No reviews yet. Be the first to share your experience!</p>
              </article>
            </div>
          )}
        </div>
      </div>

      <div className="review-form-block">
        <h3>Leave a Review</h3>
        <JotformReviewEmbed />
      </div>
    </div>
  );
};

const formatReviewDate = (isoDate) => {
  if (!isoDate) return null;
  const [year, month, day] = isoDate.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

export default function ProductPageClient({ product, sectionsHtml, medicalReview }) {
  const [activeSection, setActiveSection] = useState(TAB_SECTIONS[0]);
  const [accordionState, setAccordionState] = useState({
    "pharmacy-info": true,
  });
  const sectionRefs = useRef({});
  const [showFaxModal, setShowFaxModal] = useState(false);
  const [showEprescribeModal, setShowEprescribeModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);

  const reviews = Array.isArray(product.reviews) ? product.reviews : [];
  const reviewCount = reviews.length;
  const averageRating =
    reviewCount > 0
      ? (
          reviews.reduce((sum, review) => {
            const ratings = review.ratings || {};
            const values = ["quality", "value", "price"]
              .map((key) => Number(ratings[key] ?? 0))
              .filter((n) => n > 0);
            const avg =
              values.length > 0
                ? values.reduce((a, b) => a + b, 0) / values.length
                : 0;
            return sum + avg;
          }, 0) / reviewCount
        ).toFixed(1)
      : null;

  const formImages = (product.options?.forms || [])
    .map((form) => form.image)
    .filter(Boolean);
  const uniqueFormImages = Array.from(new Set(formImages));
  const showCarousel = !selectedForm && uniqueFormImages.length > 1;
  const fallbackImage = product.image || uniqueFormImages[0];
  const displayImage = selectedForm?.image || fallbackImage;
  const carouselImages = uniqueFormImages.length ? uniqueFormImages : [fallbackImage];
  const activeCarouselImage =
    carouselImages[carouselIndex % carouselImages.length] || fallbackImage;

  const goPrev = () => {
    if (!carouselImages.length) return;
    setCarouselIndex((prev) =>
      prev === 0 ? carouselImages.length - 1 : prev - 1
    );
  };

  const goNext = () => {
    if (!carouselImages.length) return;
    setCarouselIndex((prev) => (prev + 1) % carouselImages.length);
  };

  // #write-review deep link: expand the reviews accordion (mobile) so the form is visible.
  useEffect(() => {
    const openReviews = () => {
      if (window.location.hash !== "#write-review") return;
      setAccordionState((prev) => ({ ...prev, reviews: true }));
    };

    openReviews();
    window.addEventListener("hashchange", openReviews);
    return () => window.removeEventListener("hashchange", openReviews);
  }, []);

  useEffect(() => {
    const THRESHOLD = 170; // header (~100px) + sticky nav (~65px) + small buffer

    const handleScroll = () => {
      let current = TAB_SECTIONS[0];
      for (const section of TAB_SECTIONS) {
        const el = sectionRefs.current[section];
        if (!el) continue;
        if (el.getBoundingClientRect().top <= THRESHOLD) {
          current = section;
        }
      }
      setActiveSection(current);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // Expand it first in case the mobile accordion is showing.
    setAccordionState((prev) => ({ ...prev, [sectionId]: true }));
    document.getElementById(`sec-${sectionId}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const scrollToReviews = () => scrollToSection("reviews");

  return (
    <div id="product-page">
      <div id="product-page-inner">

        {/* TOP SECTION */}
        <div id="product-top">

          {/* LEFT: IMAGE */}
          <div id="product-image-wrap">
            <h1 id="product-title">{product.name}</h1>
            {medicalReview && (
              <a
                href={`/about/our-team/${medicalReview.reviewer.slug}/`}
                className="mb-3 flex w-full max-w-[380px] items-center gap-3 rounded-2xl bg-white/80 px-4 py-3 text-left ring-1 ring-[#d9eadb] shadow-sm transition-colors hover:ring-[#48A018]"
              >
                <EmployeeAvatar
                  employee={medicalReview.reviewer}
                  size={44}
                  className="shrink-0"
                />
                <span className="min-w-0 leading-snug">
                  <span className="block text-[11px] font-semibold uppercase tracking-[0.08em] text-[#48A018]">
                    Medically reviewed by
                  </span>
                  <span className="block text-sm font-semibold text-[#1f3d1f]">
                    {medicalReview.reviewer.name.split(",")[0]}
                    {medicalReview.reviewer.name.includes(",") && (
                      <span className="whitespace-nowrap font-normal text-[#4b5b4b]">
                        , {medicalReview.reviewer.name.split(",").slice(1).join(",").trim()}
                      </span>
                    )}
                  </span>
                  <span className="mt-0.5 block text-xs text-[#607065]">
                    {medicalReview.reviewer.title}
                    {medicalReview.lastReviewed && (
                      <>
                        {" · "}
                        <span className="whitespace-nowrap">
                          Last reviewed {formatReviewDate(medicalReview.lastReviewed)}
                        </span>
                      </>
                    )}
                  </span>
                </span>
              </a>
            )}
            {averageRating && (
              <div className="product-review-summary">
                <span className="stars">
                  {Array.from({ length: 5 }, (_, idx) => (
                    <svg
                      key={idx}
                      viewBox="0 0 20 20"
                      aria-hidden="true"
                      className={
                        idx + 1 <= Math.round(Number(averageRating))
                          ? "star filled"
                          : "star"
                      }
                    >
                      <path d="M10 1.5l2.6 5.3 5.8.8-4.2 4.1 1 5.8-5.2-2.8-5.2 2.8 1-5.8-4.2-4.1 5.8-.8L10 1.5z" />
                    </svg>
                  ))}
                </span>
                <span className="rating-text">
                  {averageRating} (
                  <button type="button" className="rating-link" onClick={scrollToReviews}>
                    {reviewCount} reviews
                  </button>
                  )
                </span>
                <button type="button" className="review-link" onClick={scrollToReviews}>
                  Add your review
                </button>
              </div>
            )}
            {showCarousel ? (
              <div className="product-image-carousel">
                <div className="carousel-image-wrap">
                  <ProductImage
                    product={product}
                    filename={activeCarouselImage}
                    alt={product["image-alt"] || product.name}
                    id="product-image"
                    width={600}
                    height={600}
                    fetchPriority="high"
                  />
                  <div className="carousel-nav">
                    <button
                      type="button"
                      className="carousel-btn"
                      onClick={goPrev}
                      aria-label="Previous image"
                    >
                      ‹
                    </button>
                    <button
                      type="button"
                      className="carousel-btn"
                      onClick={goNext}
                      aria-label="Next image"
                    >
                      ›
                    </button>
                  </div>
                </div>
                <div className="carousel-dots" role="tablist" aria-label="Product images">
                  {carouselImages.map((img, idx) => (
                    <button
                      key={img}
                      type="button"
                      role="tab"
                      aria-selected={idx === carouselIndex}
                      className={`carousel-dot ${idx === carouselIndex ? "active" : ""}`}
                      aria-label={`View image ${idx + 1}`}
                      onClick={() => setCarouselIndex(idx)}
                    />
                  ))}
                </div>
              </div>
            ) : (
              <ProductImage
                product={product}
                filename={displayImage}
                alt={product["image-alt"] || product.name}
                id="product-image"
                width={600}
                height={600}
                fetchPriority="high"
              />
            )}
            <p id="product-description" dangerouslySetInnerHTML={{ __html: product.description }} />
          </div>

          {/* RIGHT: DETAILS */}
          <div id="product-info">

            <ProductForm product={product} onFormChange={setSelectedForm} />

            <div id="product-extra-boxes">

              {/* PRESCRIPTION REQUIRED */}
              <div className="rx-box">
                <div className="rx-header">
                  <span className="rx-icon">🧾</span>
                  <h2>This medication requires a prescription.</h2>
                </div>

                <p>
                  To process your order we will require a prescription from you or your Prescriber.
                  We will only compound and dispense upon receipt of a valid patient specific prescription.
                </p>

                <ul>
                  <li>Your prescriber can mail or fax in the prescription</li>
                  <li>You can mail in the original prescription</li>
                  <li>We can contact your prescriber for you</li>
                  <li>You can transfer your prescription from another pharmacy</li>
                </ul>

                <div className="cta-actions compact">
                  <a href="/patient/" className="cta-card primary rx-theme">
                    <div className="cta-icon">📥</div>
                    <div>
                      <span>Start the Prescription</span>
                      <p>Send us your Rx details to begin processing.</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* PRESCRIBER INFO BOX */}
              <div className="prescriber-box">
                <div className="prescriber-header">
                  <img src="/images/icon-doctor.png" alt="" className="prescriber-img" />
                  <h2>Are you a licensed Prescriber?</h2>
                </div>

                <p className="prescriber-intro">
                  Choose the option that best fits how you’d like to send scripts or connect with us.
                </p>

                <div className="cta-actions prescriber-actions">
                  <PhoneLink href="tel:18449227379" className="cta-card prescriber-theme" onceKey={product.slug}>
                    <div className="cta-icon">📠</div>
                    <div>
                      <span>Fax Your Prescription</span>
                      <p>Fax (800) 786-1405 or (844) 922-7379 and we’ll process it right away.</p>
                    </div>
                  </PhoneLink>

                  <button
                    type="button"
                    className="cta-card prescriber-theme"
                    onClick={() => setShowEprescribeModal(true)}
                  >
                    <div className="cta-icon">💻</div>
                    <div>
                      <span>Send Electronically</span>
                      <p>Search CareFirst Specialty Pharmacy, Mount Laurel NJ · NCPDP #3151266.</p>
                    </div>
                  </button>

                  <PhoneLink href="tel:18448227379" className="cta-card prescriber-theme" onceKey={product.slug}>
                    <div className="cta-icon">📞</div>
                    <div>
                      <span>Phone In</span>
                      <p>Call our pharmacists at (844) 822-7379 with prescription details.</p>
                    </div>
                  </PhoneLink>

                  <a href="/prescribers/" className="cta-card prescriber-theme">
                    <div className="cta-icon">📝</div>
                    <div>
                      <span>Request More Info</span>
                      <p>Complete the prescriber form and we’ll follow up quickly.</p>
                    </div>
                  </a>

                  <a href="mailto:csr@cfspharmacy.com" className="cta-card prescriber-theme">
                    <div className="cta-icon">✉️</div>
                    <div>
                      <span>Email Our Team</span>
                      <p>csr@cfspharmacy.com for questions, refills, or status updates.</p>
                    </div>
                  </a>
                </div>
              </div>

              {/* DIFFERENT STRENGTH BOX */}
              <div className="strength-box">
                <div className="strength-header">
                  <img src="/images/icon-dog.png" alt="" className="strength-img" />
                  <h2>Looking for a Different Strength?</h2>
                </div>

                <div className="cta-actions compact">
                  <a href="/patient/" className="cta-card secondary strength-theme">
                    <div className="cta-icon">📨</div>
                    <div>
                      <span>Request a Custom Quote</span>
                      <p>Let us know the strength you need and we’ll follow up.</p>
                    </div>
                  </a>
                </div>
              </div>

            </div>

          </div>
        </div>

        {/* SCROLLSPY SECTION */}
        <div id="product-sections">

          {/* Sticky scrollspy nav (desktop) */}
          <div className="scrollspy-nav-wrap">
            <div className="scrollspy-nav">
              {TAB_SECTIONS.map((section) => {
                const { label, Icon } = SECTION_META[section];
                return (
                  <button
                    key={section}
                    type="button"
                    onClick={() => scrollToSection(section)}
                    className={`scrollspy-nav-btn ${activeSection === section ? "active" : ""}`}
                  >
                    <Icon className="scrollspy-nav-icon" aria-hidden="true" />
                    <span>{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sections render once: stacked with headings on desktop, an accordion on mobile (CSS switches at 768px) */}
          <div className="sections-stack">
            {TAB_SECTIONS.map((section) => {
              const { label, Icon } = SECTION_META[section];
              const isOpen = accordionState[section];
              return (
                <section
                  key={section}
                  id={`sec-${section}`}
                  className={`product-section ${section === "reviews" ? "product-section-reviews" : ""}`}
                  ref={(el) => { sectionRefs.current[section] = el; }}
                >
                  <div className="product-section-heading">
                    <Icon aria-hidden="true" />
                    <span>{label}</span>
                  </div>
                  <button
                    type="button"
                    className={`accordion-header ${isOpen ? "open" : ""}`}
                    aria-expanded={Boolean(isOpen)}
                    aria-controls={`sec-${section}-content`}
                    onClick={() =>
                      setAccordionState((prev) => ({
                        ...prev,
                        [section]: !prev[section],
                      }))
                    }
                  >
                    {label}
                  </button>
                  <div id={`sec-${section}-content`} className={`accordion-content ${isOpen ? "open" : ""}`}>
                    {section === "reviews" ? (
                      <>
                        <div className="section-header">
                          <h2>Customer Reviews</h2>
                          <p>{reviewCount > 0 ? `${reviewCount} review${reviewCount > 1 ? "s" : ""}` : "No reviews yet"}</p>
                        </div>
                        <ReviewsBlock reviews={reviews} />
                      </>
                    ) : (
                      <div
                        className="accordion-html"
                        dangerouslySetInnerHTML={{ __html: sectionsHtml[section] || "" }}
                      />
                    )}
                  </div>
                </section>
              );
            })}
          </div>
        </div>

        {/* Fax modal */}
        {showFaxModal && (
          <div className="modal-backdrop" onClick={() => setShowFaxModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h4>Fax Prescription Details</h4>
              <p>Please fax to (800) 786-1405 or (844) 922-7379.</p>
              <button className="btn" onClick={() => setShowFaxModal(false)}>Close</button>
            </div>
          </div>
        )}

        {/* Eprescribe modal */}
        {showEprescribeModal && (
          <div className="modal-backdrop" onClick={() => setShowEprescribeModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h4>Send Electronically</h4>
              <p className="font-semibold">CareFirst Specialty Pharmacy, LLC</p>
              <p>400 Fellowship Road, Suite 100<br />Mount Laurel, NJ 08054</p>
              <p className="mt-1">NCPDP #3151266</p>
              <button className="btn" onClick={() => setShowEprescribeModal(false)}>Close</button>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
